/* eslint-disable react/prop-types */
import { useState } from 'react';
import { useFetchChargingSession } from './Hook/Index';
import {
  Table,
  Row,
  Col,
  Card,
  Empty,
  Alert,
  Grid,
} from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { downloadExcel } from '../../utils/downloadExcel';
import PropTypes from 'prop-types';

import { useOperatorStore } from '../../store/useOperatorStore';
import SharedFilters from './SharedFilters';
import { useAuthStore } from '../../store/useAuthStore';
import ReportTrendChart from './ReportTrendChart';

const { useBreakpoint } = Grid;

export default function ChargingSessionTab({ operatorId, active = false }) {
  const { t } = useTranslation('reportPage');
  const { name } = useOperatorStore();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
    dayjs().format('YYYY-MM-DD'),
  ]);
  const { user } = useAuthStore();
  const [rfidFilter, setRfidFilter] = useState('ALL'); // Default to showing all data
  const [chargerId, setChargerId] = useState(null);
  const [locationId, setLocationId] = useState(null);

  const isRFIDValue = rfidFilter === 'ALL' ? undefined : rfidFilter === 'RFID' ? true : false;
  const { data, isLoading } = useFetchChargingSession(
    {
      operatorId,
      fromDate: dateRange[0],
      toDate: dateRange[1],
      chargerId,
      locationId,
      isRFID: isRFIDValue,
    },
    { enabled: active }
  );

  const sessionRows = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];

  const aggregateData = (rows) => {
    if (!Array.isArray(rows) || !rows.length) return null;

    return rows.reduce(
      (acc, record) => {
        acc.totalSessions += record.totalSessions || 0;
        acc.failedSessions += record.failedSessions || 0;
        acc.successSessions += record.successSessions || 0;
        acc.interruptedSessions += record.interruptedSessions || 0;
        return acc;
      },
      {
        totalSessions: 0,
        failedSessions: 0,
        successSessions: 0,
        interruptedSessions: 0,
      }
    );
  };

  const aggregatedData = operatorId ? sessionRows[0] : aggregateData(sessionRows);

  const handleExportCsv = async () => {
    const csvData = sessionRows.length
      ? sessionRows.map((record) => ({
          OperatorName: record.operatorName || name,
          TotalSessions: record.totalSessions,
          FailedSessions: record.failedSessions,
          SuccessSessions: record.successSessions,
          InterruptedSessions: record.interruptedSessions,
        }))
      : (data?.series || []).map((record) => ({
          Date: record.date,
          SuccessSessions: record.successSessions,
          FailedSessions: record.failedSessions,
          InterruptedSessions: record.interruptedSessions,
        }));
    if (!csvData.length) return;
    await downloadExcel(csvData, 'ChargingSession.xlsx');
  };

  const columns = [
    {
      title: t('operatorName'),
      dataIndex: 'operatorName',
      key: 'operatorName',
      align: 'center',
      width: isMobile ? 120 : 150,
      render: (text) => <>{text || name}</>,
    },
    {
      title: t('totalSessions'),
      dataIndex: 'totalSessions',
      key: 'totalSessions',
      align: 'center',
      width: isMobile ? 100 : 120,
    },
    {
      title: t('failedSessions'),
      dataIndex: 'failedSessions',
      key: 'failedSessions',
      align: 'center',
      width: isMobile ? 100 : 120,
    },
    {
      title: t('successSessions'),
      dataIndex: 'successSessions',
      key: 'successSessions',
      align: 'center',
      width: isMobile ? 100 : 120,
    },
    {
      title: t('interruptedSessions'),
      dataIndex: 'interruptedSessions',
      key: 'interruptedSessions',
      align: 'center',
      width: isMobile ? 120 : 150,
    },
  ];

  const handleFiltersChange = (filters) => {
    setDateRange(filters.dateRange);
    setRfidFilter(filters.rfidFilter);
    setChargerId(filters.chargerId);
    setLocationId(filters.locationId);
  };

  return (
    <div>
      {operatorId &&   user?.role === 'ADMIN' && (
        <Alert
          message={t('dashboard:alertMessage', {
            operatorName: name,
          })}
          type="info"
          showIcon
          closable
          style={{
            marginBottom: 24,
            border: 'none',
            backgroundColor: '#d7e1dd',
          }}
        />
      )}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-4">
        <SharedFilters
          operatorId={operatorId}
          onFiltersChange={handleFiltersChange}
          onExport={handleExportCsv}
          isLoading={isLoading}
        />
    
      </div>
      <Row gutter={isMobile ? 8 : 16} className="mb-4">
        <Col span={isMobile ? 12 : 6}>
          <Card size="small" title={t('totalSessions')} loading={isLoading}>
            <div className="text-2xl text-center">
              {aggregatedData?.totalSessions || 0}
            </div>
          </Card>
        </Col>
        <Col span={isMobile ? 12 : 6}>
          <Card size="small" title={t('failedSessions')} loading={isLoading}>
            <div className="text-2xl text-center">
              {aggregatedData?.failedSessions || 0}
            </div>
          </Card>
        </Col>
        <Col span={isMobile ? 12 : 6}>
          <Card size="small" title={t('successSessions')} loading={isLoading}>
            <div className="text-2xl text-center">
              {aggregatedData?.successSessions || 0}
            </div>
          </Card>
        </Col>
        <Col span={isMobile ? 12 : 6}>
          <Card size="small" title={t('interruptedSessions')} loading={isLoading}>
            <div className="text-2xl text-center">
              {aggregatedData?.interruptedSessions || 0}
            </div>
          </Card>
        </Col>
      </Row>
      <ReportTrendChart
        title={t('sessionsTrend')}
        series={data?.series}
        loading={isLoading}
        fields={[
          { key: 'successSessions', label: t('successSessions') },
          { key: 'failedSessions', label: t('failedSessions') },
          { key: 'interruptedSessions', label: t('interruptedSessions') },
        ]}
      />
      {!operatorId && (
        <div className="overflow-x-auto mt-4">
          {sessionRows.length > 0 ? (
            <Table
              columns={columns}
              dataSource={sessionRows}
              loading={isLoading}
              rowKey="_id"
              pagination={false}
              scroll={{ x: isMobile ? 600 : undefined }}
            />
          ) : (
            !isLoading && <Empty description={t('noDataAvailable')} />
          )}
        </div>
      )}
    </div>
  );
}

ChargingSessionTab.propTypes = {
  operatorId: PropTypes.string,
  active: PropTypes.bool,
};
